if (!customElements.get('recipient-form')) {
  customElements.define('recipient-form', class RecipientForm extends HTMLElement {
    constructor() {
      super();
      this.checkboxInput = this.querySelector(`#Recipient-Checkbox-${ this.dataset.sectionId }`);
      this.checkboxInput.disabled = false;
      this.hiddenControlField = this.querySelector(`#Recipient-Control-${ this.dataset.sectionId }`);
      this.hiddenControlField.disabled = true;
      this.emailInput = this.querySelector(`#Recipient-email-${ this.dataset.sectionId }`);
      // Required only once this script runs: without it the email is sent as optional.
      this.emailInput.setAttribute('aria-required', 'true');
      this.nameInput = this.querySelector(`#Recipient-name-${ this.dataset.sectionId }`);
      this.messageInput = this.querySelector(`#Recipient-message-${ this.dataset.sectionId }`);
      // Named after Shopify's error key, so displayErrorMessage finds it as this[`${key}Input`].
      this.send_onInput = this.querySelector(`#Recipient-send_on-${ this.dataset.sectionId }`);
      // The hint under a field stays in its description next to any error.
      this.hintIds = {
        message: `Recipient-message-hint-${ this.dataset.sectionId }`,
        send_on: `Recipient-send_on-hint-${ this.dataset.sectionId }`
      };
      this.offsetProperty = this.querySelector(`#Recipient-timezone-offset-${ this.dataset.sectionId }`);
      if (this.offsetProperty) this.offsetProperty.value = new Date().getTimezoneOffset().toString();
      this.setSendOnRange();
      this.sendOnField = this.querySelector('.recipient-send-on-field');
      this.sendOnValueInput = this.send_onInput;
      if (this.sendOnValueInput) {
        if (window.matchMedia('(pointer: fine)').matches) this.setUpCalendar();
        this.sendOnValueInput.addEventListener('input', () => this.renderSendOn());
        this.renderSendOn();
      }
      this.errorMessageWrapper = this.querySelector('.product-form__recipient-error-message-wrapper');
      this.errorMessageList = this.errorMessageWrapper?.querySelector('ul');
      this.errorMessage = this.errorMessageWrapper?.querySelector('.error-message');
      this.defaultErrorHeader = this.errorMessage?.innerText;
      this.currentProductVariantId = this.dataset.productVariantId;
      this.addEventListener('change', this.onChange.bind(this));
      // The browser can restore a ticked or unticked box on reload, so the fields follow it from the start.
      this.onChange();
    }

    cartUpdateUnsubscriber = undefined;
    variantChangeUnsubscriber = undefined;
    cartErrorUnsubscriber = undefined;

    connectedCallback() {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event.source === 'product-form' && event.productVariantId.toString() === this.currentProductVariantId) {
          this.resetRecipientForm();
        }
      });

      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        if (event.data.sectionId === this.dataset.sectionId) {
          this.currentProductVariantId = event.data.variant.id.toString();
        }
      });

      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, (event) => {
        if (event.source === 'product-form' && event.productVariantId.toString() === this.currentProductVariantId) {
          this.displayErrorMessage(event.message, event.errors);
        }
      });
    }

    disconnectedCallback() {
      if (this.cartUpdateUnsubscriber) {
        this.cartUpdateUnsubscriber();
      }

      if (this.variantChangeUnsubscriber) {
        this.variantChangeUnsubscriber();
      }

      if (this.cartErrorUnsubscriber) {
        this.cartErrorUnsubscriber();
      }
    }

    onChange(event) {
      const isGift = this.checkboxInput.checked;
      // Shopify reads the date in the buyer's time zone, sent only with a gift.
      if (this.offsetProperty) this.offsetProperty.disabled = !isGift;
      // Disabled fields don't post, so a card bought for oneself sends no empty recipient lines,
      // and what the buyer typed is still there if they tick the box again.
      [this.emailInput, this.nameInput, this.messageInput, this.sendOnValueInput, this.calendarButton].forEach((input) => {
        if (input) input.disabled = !isGift;
      });
      if (!isGift) {
        this.closeCalendar(false);
        if (event) this.clearErrorMessage();
      }
    }

    // The page may be cached from an earlier day, so the range follows the visitor's own today.
    // Shopify sends a scheduled card at most 90 days ahead.
    setSendOnRange() {
      if (!this.send_onInput) return;
      const today = new Date();
      const last = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 90);
      this.send_onInput.min = this.toDateValue(today);
      this.send_onInput.max = this.toDateValue(last);
    }

    // The day as YYYY-MM-DD in local time, the format Shopify reads, and back.
    toDateValue(date) {
      const pad = (number) => String(number).padStart(2, '0');
      return `${ date.getFullYear() }-${ pad(date.getMonth() + 1) }-${ pad(date.getDate()) }`;
    }

    fromDateValue(value) {
      const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '');
      return match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : null;
    }

    formatDate(date, options) {
      try {
        return new Intl.DateTimeFormat(document.documentElement.lang || undefined, options).format(date);
      } catch (error) {
        return new Intl.DateTimeFormat(undefined, options).format(date);
      }
    }

    // With a mouse the date input becomes a hidden field, and a button in the same field style
    // opens a calendar under it. The hidden field keeps the name, value, min and max.
    setUpCalendar() {
      const input = this.sendOnValueInput;
      const field = this.sendOnField;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = input.className;
      button.id = input.id;
      input.id = `${ input.id }-value`;
      input.type = 'hidden';
      button.setAttribute('aria-haspopup', 'dialog');
      button.setAttribute('aria-expanded', 'false');
      button.setAttribute('aria-describedby', input.getAttribute('aria-describedby') || '');
      if (input.getAttribute('aria-invalid')) button.setAttribute('aria-invalid', input.getAttribute('aria-invalid'));
      input.removeAttribute('aria-describedby');
      field.insertBefore(button, input);
      this.calendarButton = button;
      // displayErrorMessage marks this[`${key}Input`] as invalid, and that is now the button.
      this.send_onInput = button;

      const titleId = `Recipient-send_on-month-${ this.dataset.sectionId }`;
      const calendar = document.createElement('div');
      calendar.className = 'recipient-calendar';
      calendar.hidden = true;
      calendar.setAttribute('role', 'dialog');
      calendar.setAttribute('aria-modal', 'false');
      calendar.setAttribute('aria-labelledby', `Recipient-send_on-label-${ this.dataset.sectionId }`);
      calendar.innerHTML = `
        <div class="recipient-calendar__head">
          <button type="button" class="recipient-calendar__nav" data-month="-1">${ this.chevron('M12.5 15l-5-5 5-5') }</button>
          <p class="recipient-calendar__title" id="${ titleId }" aria-live="polite"></p>
          <button type="button" class="recipient-calendar__nav" data-month="1">${ this.chevron('M7.5 5l5 5-5 5') }</button>
        </div>
        <table class="recipient-calendar__grid" role="grid" aria-labelledby="${ titleId }"><thead><tr></tr></thead><tbody></tbody></table>
        <div class="recipient-calendar__foot">
          <button type="button" class="recipient-calendar__clear"></button>
        </div>`;
      calendar.querySelector('.recipient-calendar__clear').textContent = field.dataset.clearLabel;
      const [previous, next] = calendar.querySelectorAll('.recipient-calendar__nav');
      previous.setAttribute('aria-label', field.dataset.previousLabel);
      next.setAttribute('aria-label', field.dataset.nextLabel);
      field.appendChild(calendar);
      this.calendar = calendar;

      // Monday-first weekday initials in the page language: 1 January 2024 was a Monday.
      const weekdays = calendar.querySelector('thead tr');
      for (let index = 0; index < 7; index += 1) {
        const day = new Date(2024, 0, 1 + index);
        const th = document.createElement('th');
        th.scope = 'col';
        th.abbr = this.formatDate(day, { weekday: 'long' });
        th.textContent = this.formatDate(day, { weekday: 'narrow' });
        weekdays.appendChild(th);
      }

      button.addEventListener('click', () => (calendar.hidden ? this.openCalendar() : this.closeCalendar(true)));
      button.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown' && calendar.hidden) {
          event.preventDefault();
          this.openCalendar();
        }
      });
      calendar.addEventListener('click', (event) => {
        const target = event.target.closest('button');
        if (!target || target.disabled) return;
        if (target.dataset.month) {
          this.moveFocusDay(this.addMonths(this.focusDay, Number(target.dataset.month)), false);
          // At the first or last month the arrow turns disabled and would drop focus.
          if (target.disabled) this.calendar.querySelector('[tabindex="0"]')?.focus();
        }
        else if (target.dataset.date) this.pickDate(target.dataset.date);
        else if (target.classList.contains('recipient-calendar__clear')) this.pickDate('');
      });
      calendar.addEventListener('keydown', (event) => this.onCalendarKeydown(event));
      // The field's label clicks the button, which closes the open calendar on its own.
      const label = document.getElementById(`Recipient-send_on-label-${ this.dataset.sectionId }`);
      this.onOutsidePointer = (event) => {
        if (!field.contains(event.target) && !label?.contains(event.target)) this.closeCalendar(false);
      };
      field.addEventListener('focusout', (event) => {
        if (event.relatedTarget && !field.contains(event.relatedTarget)) this.closeCalendar(false);
      });
    }

    chevron(path) {
      return `<svg aria-hidden="true" focusable="false" width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="${ path }"/></svg>`;
    }

    sendOnRange() {
      return {
        min: this.fromDateValue(this.sendOnValueInput.min),
        max: this.fromDateValue(this.sendOnValueInput.max)
      };
    }

    clampDay(date) {
      const { min, max } = this.sendOnRange();
      if (min && date < min) return min;
      if (max && date > max) return max;
      return date;
    }

    addMonths(date, months) {
      const target = new Date(date.getFullYear(), date.getMonth() + months, 1);
      const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
      target.setDate(Math.min(date.getDate(), lastDay));
      return target;
    }

    openCalendar() {
      const today = new Date();
      this.focusDay = this.clampDay(this.fromDateValue(this.sendOnValueInput.value) || new Date(today.getFullYear(), today.getMonth(), today.getDate()));
      this.renderCalendar();
      this.calendar.hidden = false;
      this.sendOnField.toggleAttribute('data-open', true);
      this.calendarButton.setAttribute('aria-expanded', 'true');
      document.addEventListener('pointerdown', this.onOutsidePointer);
      this.calendar.querySelector('[tabindex="0"]')?.focus();
    }

    closeCalendar(returnFocus) {
      if (!this.calendar || this.calendar.hidden) return;
      this.calendar.hidden = true;
      this.sendOnField.toggleAttribute('data-open', false);
      this.calendarButton.setAttribute('aria-expanded', 'false');
      document.removeEventListener('pointerdown', this.onOutsidePointer);
      if (returnFocus) this.calendarButton.focus();
    }

    pickDate(value) {
      this.sendOnValueInput.value = value;
      this.sendOnValueInput.dispatchEvent(new Event('input', { bubbles: true }));
      this.sendOnValueInput.dispatchEvent(new Event('change', { bubbles: true }));
      this.closeCalendar(true);
    }

    moveFocusDay(date, focus = true) {
      this.focusDay = this.clampDay(date);
      this.renderCalendar();
      if (focus) this.calendar.querySelector('[tabindex="0"]')?.focus();
    }

    onCalendarKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.closeCalendar(true);
        return;
      }
      if (!event.target.dataset.date) return;
      const day = this.focusDay;
      const mondayIndex = (day.getDay() + 6) % 7;
      const steps = {
        ArrowLeft: () => new Date(day.getFullYear(), day.getMonth(), day.getDate() - 1),
        ArrowRight: () => new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1),
        ArrowUp: () => new Date(day.getFullYear(), day.getMonth(), day.getDate() - 7),
        ArrowDown: () => new Date(day.getFullYear(), day.getMonth(), day.getDate() + 7),
        Home: () => new Date(day.getFullYear(), day.getMonth(), day.getDate() - mondayIndex),
        End: () => new Date(day.getFullYear(), day.getMonth(), day.getDate() + 6 - mondayIndex),
        PageUp: () => this.addMonths(day, -1),
        PageDown: () => this.addMonths(day, 1)
      };
      if (!steps[event.key]) return;
      event.preventDefault();
      this.moveFocusDay(steps[event.key]());
    }

    renderCalendar() {
      const { min, max } = this.sendOnRange();
      const selected = this.sendOnValueInput.value;
      const todayValue = this.toDateValue(new Date());
      const focusValue = this.toDateValue(this.focusDay);
      const year = this.focusDay.getFullYear();
      const month = this.focusDay.getMonth();
      const title = this.formatDate(new Date(year, month, 1), { month: 'long', year: 'numeric' });
      this.calendar.querySelector('.recipient-calendar__title').textContent = title.charAt(0).toUpperCase() + title.slice(1);

      const [previous, next] = this.calendar.querySelectorAll('.recipient-calendar__nav');
      const nextMonth = new Date(year, month + 1, 1);
      previous.disabled = Boolean(min) && new Date(year, month, 0) < min;
      next.disabled = Boolean(max) && nextMonth > max;

      const body = this.calendar.querySelector('tbody');
      body.innerHTML = '';
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const lead = (new Date(year, month, 1).getDay() + 6) % 7;
      let row;
      for (let cell = 0; cell < Math.ceil((lead + daysInMonth) / 7) * 7; cell += 1) {
        if (cell % 7 === 0) row = body.insertRow();
        const td = row.insertCell();
        const dayNumber = cell - lead + 1;
        if (dayNumber < 1 || dayNumber > daysInMonth) continue;
        const date = new Date(year, month, dayNumber);
        const value = this.toDateValue(date);
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'recipient-calendar__day';
        button.dataset.date = value;
        button.textContent = dayNumber;
        button.tabIndex = value === focusValue ? 0 : -1;
        button.setAttribute('aria-label', this.formatDate(date, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }));
        if (value === todayValue) button.setAttribute('aria-current', 'date');
        button.disabled = Boolean(min && date < min) || Boolean(max && date > max);
        td.setAttribute('role', 'gridcell');
        // ARIA reads the chosen day from the gridcell, as in the APG date picker, not from its button.
        td.setAttribute('aria-selected', String(value === selected));
        td.appendChild(button);
      }
    }

    // The field shows the chosen day in the page language, or the placeholder when empty.
    renderSendOn() {
      const date = this.fromDateValue(this.sendOnValueInput.value);
      if (this.sendOnField) this.sendOnField.toggleAttribute('data-empty', !date);
      if (!this.calendarButton) return;
      const text = date ? this.formatDate(date, { day: 'numeric', month: 'long', year: 'numeric' }) : '';
      const label = document.getElementById(`Recipient-send_on-label-${ this.dataset.sectionId }`)?.textContent.trim();
      this.calendarButton.textContent = text;
      this.calendarButton.setAttribute('aria-label', `${ label }: ${ text || this.sendOnField.dataset.chooseLabel }`);
    }

    clearInputFields(flag = false) {
      this.emailInput.value = '';
      this.nameInput.value = '';
      this.messageInput.value = '';
      if (this.sendOnValueInput) {
        this.sendOnValueInput.value = '';
        this.closeCalendar(false);
        this.renderSendOn();
      }
      if(flag) this.checkboxInput.checked = false;;
    }

    displayErrorMessage(title, body) {
      this.clearErrorMessage();
      this.errorMessageWrapper.hidden = false;
      if (typeof body === 'object') {
        this.errorMessage.innerText = this.defaultErrorHeader;
        return Object.entries(body).forEach(([key, value]) => {
          const errorMessageId = `RecipientForm-${ key }-error-${ this.dataset.sectionId }`
          const fieldSelector = `#Recipient-${ key }-${ this.dataset.sectionId }`;
          const message = Array.isArray(value) ? value.join(', ') : value;
          const errorMessageElement = this.querySelector(`#${errorMessageId}`);
          const errorTextElement = errorMessageElement?.querySelector('.error-message')
          if (!errorTextElement) return;

          if (this.errorMessageList) {
            this.errorMessageList.appendChild(this.createErrorListItem(fieldSelector, message));
          }

          errorTextElement.innerText = `${message}.`;
          errorMessageElement.classList.remove('hidden');

          const inputElement = this[`${key}Input`];
          if (!inputElement) return;

          inputElement.setAttribute('aria-invalid', true);
          inputElement.setAttribute('aria-describedby', [this.hintIds[key], errorMessageId].filter(Boolean).join(' '));
        });
      }

      this.errorMessage.innerText = body;
    }

    createErrorListItem(target, message) {
      const li = document.createElement('li');
      const a = document.createElement('a');
      a.setAttribute('href', target);
      a.innerText = message;
      li.appendChild(a);
      li.className = "error-message";
      return li;
    }

    clearErrorMessage() {
      this.errorMessageWrapper.hidden = true;

      if (this.errorMessageList) this.errorMessageList.innerHTML = '';

      this.querySelectorAll('.recipient-fields .form__message').forEach(field => {
        field.classList.add('hidden');
        const textField = field.querySelector('.error-message');
        if (textField) textField.innerText = '';
      });

      [this.emailInput, this.messageInput, this.nameInput, this.send_onInput].forEach(inputElement => {
        if (!inputElement) return;
        inputElement.setAttribute('aria-invalid', false);
        inputElement.removeAttribute('aria-describedby');
      });
      if (this.messageInput) this.messageInput.setAttribute('aria-describedby', this.hintIds.message);
      if (this.send_onInput) this.send_onInput.setAttribute('aria-describedby', this.hintIds.send_on);
    }

    // After the card goes into the cart, the next one starts from an empty form.
    resetRecipientForm() {
      if (this.checkboxInput.checked) {
        this.clearInputFields(true);
        this.onChange();
        this.clearErrorMessage();
      }
    }
  });
}
